var __assign = (this && this.__assign) || Object.assign || function(t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
        s = arguments[i];
        for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
            t[p] = s[p];
    }
    return t;
};
import "pixi.js";
import { Point, Polygon } from "pixi.js";
import { utilGrid } from "../grid/utilGrid";
import { utilLoca } from "../i18n/utilLoca";
import { BuildingStateType } from "../network/generated/protoTypes";
import { pascalSentenceFromString } from "../stringUtils";
import { utilDrawGrid } from "./utilDrawGrid";
var Container = PIXI.Container;
var Graphics = PIXI.Graphics;
var Text = PIXI.Text;
var colorFromName = function (name) {
    var upper = name.toUpperCase();
    var colorStr = parseInt(upper
        .split("")
        .map(function (letter, index) { return upper.charCodeAt(index); })
        .reduce(function (acc, value) { return acc + value; }, ""), 10).toString(16);
    return parseInt(colorStr.substr(0, 6), 16);
};
var debugGetGenericBuildingIso = function (data, volume, texts, color) {
    if (volume === void 0) { volume = 0; }
    if (texts === void 0) { texts = []; }
    if (color === void 0) { color = -1; }
    if (data.balancing == undefined) {
        return undefined;
    }
    var sizeX = data.proto.flipped ? data.balancing.yExtent : data.balancing.xExtent;
    var sizeY = data.proto.flipped ? data.balancing.xExtent : data.balancing.yExtent;
    var display = utilDrawGrid.draw(new Graphics(), {
        fillColor: color === -1 ? colorFromName(data.balancing.name) : color,
        sizeX: sizeX - 2,
        sizeY: sizeY - 2,
        volume: volume,
        x: 2,
        y: 2,
    });
    var container = new Container();
    var text = debugTextList(texts, [14, 10], [
        {},
        {
            fontStyle: "italic",
            fontWeight: "bold",
        },
    ]);
    var center = utilGrid.objectCenterOnScreen({
        id: data.proto.id,
        x: 0,
        xExtent: sizeX,
        y: 0,
        yExtent: sizeY,
    });
    text.position.set(center.x, center.y);
    container.addChild(display, text);
    container.cacheAsBitmap = true;
    container.hitArea = new Polygon([
        { x: 0, y: 0 },
        { x: sizeX - 2, y: 0 },
        { x: sizeX - 2 + volume, y: volume },
        { x: sizeX - 2 + volume, y: sizeY - 2 + volume },
        { x: volume, y: sizeY - 2 + volume },
        { x: 0, y: sizeY - 2 },
        { x: 0, y: 0 },
    ].map(function (p) {
        var pos = utilGrid.mapToScreen(p.x + 1, p.y + 1);
        return new Point(pos.x, pos.y);
    }));
    return container;
};
export var debugGetBuildingGraphicsIso = function (data) {
    return debugGetGenericBuildingIso(data, data.proto.stage * 0.5, [
        utilLoca.get(data.balancing.locaKey),
        "<" + BuildingStateType[data.proto.state || 0].toLowerCase() + "/>",
        data.production ? "\uD83C\uDFED " + data.production.recipe.name : undefined,
    ]
        .filter(function (val) { return !!val; })
        .map(pascalSentenceFromString));
};
export var debugGetBuildingHighlightIso = function (data) {
    if (data.balancing == undefined) {
        return undefined;
    }
    var sizeX = data.proto.flipped ? data.balancing.yExtent : data.balancing.xExtent;
    var sizeY = data.proto.flipped ? data.balancing.xExtent : data.balancing.yExtent;
    return utilDrawGrid.drawObjectOutline(new Graphics(), {
        fillAlpha: 0,
        fillColor: colorFromName(data.balancing.name),
        lineColor: 0xffffff,
        lineWidth: 2,
        sizeX: sizeX - 1,
        sizeY: sizeY - 1,
        volume: data.proto.stage || 0,
        x: 1,
        y: 1,
    });
};
export var debugGetUnlockedGroundGraphicIso = function (data) {
    return utilDrawGrid.drawObject(new Graphics(), {
        fillAlpha: 1,
        fillColor: 0x65340a,
        sizeX: data.xExtent,
        sizeY: data.yExtent,
        volume: 0,
        x: 0,
        y: 0,
    });
};
export var debugGetUnlockableGroundGraphicIso = function (data) {
    var graphics = utilDrawGrid.drawObject(new Graphics(), {
        fillAlpha: 0.8,
        fillColor: 0x9891ff,
        sizeX: data.xExtent,
        sizeY: data.yExtent,
        volume: 0,
        x: 0,
        y: 0,
    });
    var container = new Container();
    var unlockMeText = debugText("Buy Expansion");
    var center = utilGrid.objectCenterOnScreen(__assign({}, data, { x: 0, y: 0 }));
    unlockMeText.position.set(center.x, center.y);
    container.addChild(graphics, debugText("" + data.id, 0xcccccc, 0x555555), unlockMeText); // {${data.x}, ${data.y}}`));
    container.cursor = "pointer";
    container.interactive = true;
    container.cacheAsBitmap = true;
    container.hitArea = new Polygon([
        new Point(0, 0),
        new Point(data.xExtent, 0),
        new Point(data.xExtent, data.yExtent),
        new Point(0, data.yExtent),
        new Point(0, 0),
    ].map(function (p) {
        var pos = utilGrid.mapToScreen(p.x, p.y);
        return new Point(pos.x, pos.y);
    }));
    return container;
};
export var debugGetLockedGroundGraphicIso = function (data) {
    var graphics = utilDrawGrid.drawObject(new Graphics(), {
        fillAlpha: 0.5,
        fillColor: 0xaf4c4c,
        sizeX: data.xExtent,
        sizeY: data.yExtent,
        volume: 0,
        x: 0,
        y: 0,
    });
    var container = new Container();
    container.addChild(graphics, debugText("" + data.id, 0xcccccc, 0x555555)); // {${data.x}, ${data.y}}`));
    return container;
};
export var debugGetBuildingScaffoldingIso = function (data) {
    var texts = [
        utilLoca.get(data.balancing.locaKey),
        "<" + BuildingStateType[data.proto.state || 0].toLowerCase() + "/>",
    ]
        .filter(function (val) { return !!val; })
        .map(pascalSentenceFromString);
    var sizeX = data.proto.flipped ? data.balancing.yExtent : data.balancing.xExtent;
    var sizeY = data.proto.flipped ? data.balancing.xExtent : data.balancing.yExtent;
    var volume = data.proto.stage;
    var graphics = utilDrawGrid.drawObject(new Graphics(), {
        fillAlpha: 1,
        fillColor: 0x242424,
        lineColor: -1,
        lineWidth: 0,
        originMarkColor: -1,
        sizeX: sizeX - 1,
        sizeY: sizeY - 1,
        volume: volume,
        x: 1,
        y: 1,
    }, false);
    // Left stripes
    for (var i = 1; i < sizeY; i++) {
        utilDrawGrid.drawFill(graphics, 0xeeee00, 1, [
            { x: 1, y: i },
            { x: 1, y: i + 0.5 },
            { x: volume + 1, y: i + 1 + volume },
            { x: volume + 1, y: i + 0.5 + volume },
        ]);
    }
    // Right stripes
    for (var i = 1; i < sizeX; i++) {
        utilDrawGrid.drawFill(graphics, 0xeeee00, 1, [
            { x: i + 1, y: 1 },
            { x: i + 0.5, y: 1 },
            { x: i + volume, y: volume + 1 },
            { x: i + 0.5 + volume, y: volume + 1 },
        ]);
    }
    var container = new Container();
    var text = debugTextList(texts);
    var center = utilGrid.objectCenterOnScreen({
        id: data.proto.id,
        x: 0,
        xExtent: sizeX,
        y: 0,
        yExtent: sizeY,
    });
    text.position.set(center.x, center.y);
    container.hitArea = new Polygon([
        { x: 0, y: 0 },
        { x: sizeX - 1, y: 0 },
        { x: sizeX - 1 + volume, y: volume },
        { x: sizeX - 1 + volume, y: sizeY - 1 + volume },
        { x: volume, y: sizeY - 1 + volume },
        { x: 0, y: sizeY - 1 },
        { x: 0, y: 0 },
    ].map(function (p) {
        var pos = utilGrid.mapToScreen(p.x + 1, p.y + 1);
        return new Point(pos.x, pos.y);
    }));
    container.addChild(graphics, text);
    return container;
};
export var debugGetBuildingFinishedScaffoldingIso = function (data) {
    var display = debugGetGenericBuildingIso(data, data.proto.stage * 0.5, ["\uD83C\uDFD7 " + utilLoca.get(data.balancing.locaKey)], 0xeeeeee);
    var graphics = display.getChildAt(0);
    var sizeX = (data.proto.flipped ? data.balancing.yExtent : data.balancing.xExtent) - 1;
    var sizeY = (data.proto.flipped ? data.balancing.xExtent : data.balancing.yExtent) - 1;
    var volume = data.proto.stage || 1;
    utilDrawGrid.drawFill(graphics, 0xdd0000, 1, [
        // Front-bottom line
        { x: 0, y: (sizeY - 1) * 0.5 },
        { x: 0, y: (sizeY + 1) * 0.5 },
        // Goes up
        { x: volume, y: (sizeY + 1) * 0.5 + volume },
        // Top-center
        { x: volume + (sizeX - 1) * 0.5, y: (sizeY + 1) * 0.5 + volume },
        // Top-left
        { x: volume + (sizeX - 1) * 0.5, y: sizeY + volume },
        { x: volume + (sizeX + 1) * 0.5, y: sizeY + volume },
        // Top-center-back
        { x: volume + (sizeX + 1) * 0.5, y: (sizeY + 1) * 0.5 + volume },
        // Top-back
        { x: volume + sizeX, y: (sizeY + 1) * 0.5 + volume },
        { x: volume + sizeX, y: (sizeY - 1) * 0.5 + volume },
        // Top-center-back
        { x: volume + (sizeX + 1) * 0.5, y: (sizeY - 1) * 0.5 + volume },
        // Top-right
        { x: volume + (sizeX + 1) * 0.5, y: volume },
        // Right-bottom
        { x: (sizeX + 1) * 0.5, y: 0 },
        { x: (sizeX - 1) * 0.5, y: 0 },
        // Top-right-back
        { x: volume + (sizeX - 1) * 0.5, y: volume },
        // Top-center-back
        { x: volume + (sizeX - 1) * 0.5, y: (sizeY - 1) * 0.5 + volume },
        // Top-front
        { x: volume, y: (sizeY - 1) * 0.5 + volume },
        // Goes down
        { x: 0, y: (sizeY - 1) * 0.5 },
    ].map(function (p) {
        p.x += 1;
        p.y += 1;
        return p;
    }));
    return display;
};
export var debugText = function (value, bgColor, color, fontSize, style) {
    if (bgColor === void 0) { bgColor = 0xfefefe; }
    if (color === void 0) { color = 0x1d1938; }
    if (fontSize === void 0) { fontSize = 14; }
    if (style === void 0) { style = {}; }
    var container = new Container();
    var text = new Text(value, __assign({ align: "center", fill: "#" + color.toString(16), fontFamily: "RobotoCondensed Regular", fontSize: fontSize }, style));
    var textBg = new Graphics()
        .lineStyle(2, color)
        .beginFill(bgColor)
        .drawRect(0, 0, text.width + 16, text.height + 8)
        .endFill();
    text.position.set(8, 4);
    container.addChild(textBg, text);
    container.pivot.set(textBg.width * 0.5, textBg.height * 0.5);
    return container;
};
export var debugTextList = function (args, fontSizes, textStyles, spacing) {
    if (fontSizes === void 0) { fontSizes = [14]; }
    if (textStyles === void 0) { textStyles = [{}]; }
    if (spacing === void 0) { spacing = 1; }
    var container = new Container();
    args.forEach(function (val, index) {
        var text = debugText(val, undefined, undefined, fontSizes[Math.min(index, fontSizes.length - 1)], textStyles[Math.min(index, textStyles.length - 1)]);
        text.position.set(0, index * (text.height + spacing));
        container.addChild(text);
    });
    container.pivot.set(0, container.height * 0.5);
    return container;
};



//////////////////
// WEBPACK FOOTER
// ./src/utils/graphics/debugGraphics.ts
// module id = 2208
// module chunks = 3